'use strict';

export default class MeFinancialCtrl {
  constructor(StripeSvc, LoaderSvc, $window, NewCardModalSvc,
    NewAccountModalSvc, VerifyAccountModalSvc) {
    var that = this;

    this.cards = [];
    this.accounts = []; // Old bank accounts (Sources)
    this.bankAccounts = []; // New bank accounts (PaymentMethods)
    this.cardError = '';
    this.cardSubmitting = false;
    this.cardElement = null;
    this.stripeElements = null;
    this.cardElementMounted = false;

    // Helper to normalize bank account display fields
    this.normalizeAccount = function(account) {
      if(!account || !account.type) return {};
      let info = {};

      if(account.type === 'us_bank_account' && account.us_bank_account) {
        // eslint-disable-next-line camelcase
        info.bank_name = account.us_bank_account.bank_name;
        info.country = account.us_bank_account.country || 'US';
        info.last4 = account.us_bank_account.last4;

        // Use the new SetupIntent-based verification status logic
        info.status = account.derived_verification_status || 'unverified';

        info.id = account.id;

        // Include debugging/functional info
        /* eslint-disable camelcase */
        info.source = account.source;
        info.setup_intent_id = account.setup_intent_id;
        info.setup_intent_status = account.setup_intent_status;
        info.setup_intent_next_action = account.setup_intent_next_action;
        info.microdeposit_type = account.setup_intent_next_action
          && account.setup_intent_next_action.type
            === 'verify_with_microdeposits'
          && account.setup_intent_next_action.verify_with_microdeposits
          ? account.setup_intent_next_action
            .verify_with_microdeposits.microdeposit_type
          : null;
        info.source_api_evidence = account.source_api_evidence;
        /* eslint-enable camelcase */
      } else if(account.type === 'sepa_debit' && account.sepa_debit) {
        // eslint-disable-next-line camelcase
        info.bank_name = 'SEPA Bank';
        info.country = account.sepa_debit.country;
        info.last4 = account.sepa_debit.last4;
        info.status = account.sepa_debit.status;
        info.id = account.id;
      } else if(account.type === 'bacs_debit' && account.bacs_debit) {
        // eslint-disable-next-line camelcase
        info.bank_name = 'UK Bank';
        info.country = account.bacs_debit.country;
        info.last4 = account.bacs_debit.last4;
        info.status = account.bacs_debit.status;
        info.id = account.id;
      } else if(account.type === 'au_becs_debit' && account.au_becs_debit) {
        // eslint-disable-next-line camelcase
        info.bank_name = 'AU Bank';
        info.country = account.au_becs_debit.country;
        info.last4 = account.au_becs_debit.last4;
        info.status = account.au_becs_debit.status;
        info.id = account.id;
      } else {
        // eslint-disable-next-line camelcase
        info.bank_name = 'Unknown';
        info.country = account.country || '';
        info.last4 = account.last4 || '';
        info.status = account.status || '';
        info.id = account.id;
      }
      return info;
    };

    loadFin();

    function loadFin() {
      LoaderSvc.start();
      StripeSvc.indexUserCards().then(function(cards) {
        // Filter to only modern PaymentMethods (pm_ prefix) - no legacy tokens
        that.cards = cards.filter(function(card) {
          return card.id && card.id.startsWith('pm_');
        });
        return StripeSvc.indexUserAccounts(); // Old bank accounts (Sources)
      })
        .then(function(accounts) {
          that.accounts = accounts;
          // New bank accounts (PaymentMethods)
          return StripeSvc.indexUserPaymentMethods();
        })
        .then(function(paymentMethods) {
          // Optimized single-pass processing for US bank accounts
          const bankAccounts = [];
          for(let i = 0; i < paymentMethods.length; i++) {
            const pm = paymentMethods[i];

            // Skip non-US bank accounts early
            if(pm.type !== 'us_bank_account' || !pm.us_bank_account) continue;

            // Inline normalization for performance
            const status = pm.derived_verification_status || 'unverified';

            // Skip unverified and detached accounts early
            if(status === 'verified_but_detached'
               || status === 'unverified') continue;

            // Build normalized account object
            /* eslint-disable camelcase */
            bankAccounts.push({
              bank_name: pm.us_bank_account.bank_name,
              country: pm.us_bank_account.country || 'US',
              last4: pm.us_bank_account.last4,
              status,
              id: pm.id,
              source: pm.source,
              setup_intent_id: pm.setup_intent_id,
              setup_intent_status: pm.setup_intent_status,
              setup_intent_next_action: pm.setup_intent_next_action,
              microdeposit_type: pm.setup_intent_next_action
                && pm.setup_intent_next_action.type
                  === 'verify_with_microdeposits'
                && pm.setup_intent_next_action.verify_with_microdeposits
                ? pm.setup_intent_next_action
                  .verify_with_microdeposits.microdeposit_type
                : null,
              source_api_evidence: pm.source_api_evidence
            });
            /* eslint-enable camelcase */
          }

          that.bankAccounts = bankAccounts;
          LoaderSvc.stop();
        })
        .catch(function(error) {
          console.error('Error loading financial data:', error);
          LoaderSvc.stop();
        });
    }

    ////////////////////////////////////////////////////////////////////////////
    ///  Cards
    ////////////////////////////////////////////////////////////////////////////
    this.newCard = function() {
      $window.scrollTo(0, 0); // scroll to ensure toastr is visible
      NewCardModalSvc.open().then(loadFin);
    };

    this.deleteCard = function(cardId) {
      var msg = 'Are you sure you want to remove this card?';
      if($window.confirm(msg)) {
        LoaderSvc.start();
        StripeSvc.deleteCard(cardId).then(function() {
          LoaderSvc.stop();
          loadFin();
        });
      }
    };

    ////////////////////////////////////////////////////////////////////////////
    ///  Bank Accounts (PaymentMethods)
    ////////////////////////////////////////////////////////////////////////////
    this.newBankAccount = function() {
      $window.scrollTo(0, 0); // scroll to ensure toastr is visible
      // Use the same modal as old bank accounts but with new SetupIntent flow
      NewAccountModalSvc.open().then(loadFin);
    };

    this.verifyBankAccount = function(account) {
      $window.scrollTo(0, 0); // scroll to ensure toastr is visible
      // Use the same verification modal, but let it handle
      // different account types
      VerifyAccountModalSvc.open(account).then(loadFin);
    };

    this.deleteBankAccount = function(account) {
      var msg = 'Are you sure you want to remove bank account '
        + `${account.bank_name} ending in ${account.last4}?`;
      if($window.confirm(msg)) {
        LoaderSvc.start();

        // Use the new deleteBankAccount service that handles
        // SetupIntent cancellation
        StripeSvc.deleteBankAccount(account.id, account.setup_intent_id)
          .then(function() {
            // Optimistically remove the account from UI immediately
            that.bankAccounts = that.bankAccounts.filter(
              acc => acc.id !== account.id);

            LoaderSvc.stop();
          })
          .catch(function(error) {
            console.error('[Financial Controller] Delete error:', error);
            LoaderSvc.stop();
            // On error, refresh to restore accurate state
            loadFin();
          });
      }
    };
  }
}

MeFinancialCtrl.$inject = [
  'StripeSvc',
  'LoaderSvc',
  '$window',
  'NewCardModalSvc',
  'NewAccountModalSvc',
  'VerifyAccountModalSvc',
  '$scope',
  '$timeout'
];
